// @flow
import * as Immutable from 'immutable';

import BaseActions from 'common/redux/actions/BaseActions';
import ImmutableModel from 'common/models/flow/ImmutableModel';
import isEqual from 'lodash/isEqual';

export type ActionType = {
    type: string,
    params: Object,
    entity?: Object,
    entities?: Object,
    error?: Error,
    body?: Object
};

export const isRequestBodyCurrent = function ({
    state,
    action
}: {
    state: Immutable.Map<*, *>,
    action: ActionType
}) {
    const { params, body } = action;
    const prevRequestBody = state.getIn([
        'records',
        params.clientId,
        'entities',
        'prevRequestBody'
    ]);
    return isEqual(prevRequestBody, body);
};

const createBaseEntityReducer = function <TEntity: ImmutableModel>(
    actions: BaseActions<TEntity, *>,
    EntityClass: TEntity
) {
    return function (
        state = Immutable.fromJS({ records: {} }),
        action: ActionType
    ): Immutable.Map<string, any> {
        const { options } = action;
        switch (action.type) {
            case actions.types.FETCH_ENTITIES_START: {
                const { body } = action;
                const { clientId } = action.params;
                return state.withMutations(mutableState => {
                    mutableState.setIn(
                        [
                            'records',
                            clientId,
                            'entities',
                            'status',
                            'isLoading'
                        ],
                        true
                    );
                    mutableState.setIn(
                        [
                            'records',
                            clientId,
                            'entities',
                            'status',
                            'isPolling'
                        ],
                        options && options.isPolling
                    );

                    mutableState.setIn(
                        ['records', clientId, 'entities', 'prevRequestBody'],
                        body
                    );
                });
            }

            case actions.types.FETCH_ENTITIES_SUCCESS: {
                const { entities, params } = action;
                if (
                    !isRequestBodyCurrent({
                        state,
                        action
                    })
                ) {
                    // reject if prevRequestBody is not same as current body
                    return state;
                }
                if (!entities) {
                    throw new Error('expected action to have entities');
                }
                const { clientId } = params;
                let newState = state.setIn(
                    ['records', clientId, 'entities', 'status', 'isLoading'],
                    false
                );
                newState = newState.setIn(
                    ['records', clientId, 'entities', 'status', 'isPolling'],
                    false
                );
                const convertedEntities = entities.map(entity =>
                    EntityClass.fromJS(entity)
                );
                newState = newState.setIn(
                    ['records', clientId, 'entities', 'records'],
                    convertedEntities
                );
                newState = newState.deleteIn([
                    'records',
                    clientId,
                    'entities',
                    'error'
                ]);
                return newState;
            }

            case actions.types.FETCH_ENTITIES_FAILURE: {
                const { error, params } = action;
                const { clientId } = params;
                if (
                    !isRequestBodyCurrent({
                        state,
                        action
                    })
                ) {
                    // reject if prevRequestBody is not same as current body
                    return state;
                }
                let newState = state.setIn(
                    ['records', clientId, 'entities', 'status', 'isLoading'],
                    false
                );
                newState = newState.setIn(
                    ['records', clientId, 'entities', 'status', 'isPolling'],
                    false
                );
                newState = newState.setIn(
                    ['records', clientId, 'entities', 'error'],
                    error
                );
                return newState;
            }

            case actions.types.REFRESH_ENTITY_SUCCESS: {
                const { entity: plainEntity, params } = action;
                if (!plainEntity) {
                    throw new Error('plain entity expected');
                }
                const entity = EntityClass.fromJS(plainEntity);
                const { clientId } = params;
                const existingEntities = state.getIn([
                    'records',
                    clientId,
                    'entities',
                    'records'
                ]);
                if (!existingEntities) {
                    // There are no entities stored, probably got removed.
                    // So, no need to refresh. Bye Bye!
                    return state;
                }
                if (!entity.isSame) {
                    throw new Error('isSame method in entity not implemented');
                }
                const index = existingEntities.entities.findIndex(
                    existingEntity => existingEntity.isSame(entity)
                );
                if (index < 0) {
                    // Entity not found, probably got removed.
                    // So, no need to refresh. Yay!
                    return state;
                }
                const existingEntity = existingEntities.entities[index];
                const mergedEntity = existingEntity.mergeDeep(entity);
                const refreshedEntitiesList = Object.assign(
                    [...existingEntities.entities],
                    { [index]: mergedEntity }
                );
                return state.setIn(
                    ['records', clientId, 'entities', 'records'],
                    {
                        ...existingEntities,
                        entities: refreshedEntitiesList
                    }
                );
            }

            case actions.types.CLEAR_DATA: {
                return state.delete('records');
            }
        }
        return state;
    };
};

export default createBaseEntityReducer;
